import React, { Component, ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import Button from './Button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">حدث خطأ</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              عذراً، حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.
            </p>
            {this.state.error && import.meta.env.DEV && (
              <details className="mb-6 text-start">
                <summary className="mb-2 cursor-pointer text-sm text-gray-500 dark:text-gray-400">
                  تفاصيل الخطأ (للتطوير فقط)
                </summary>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  الرسالة التالية من محرك React قد تكون بالإنجليزية؛ هي للمطورين فقط.
                </p>
                <pre className="max-h-48 max-w-full overflow-auto whitespace-pre-wrap break-words rounded bg-gray-100 p-3 text-xs text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <div className="flex gap-4 justify-center">
              <Button variant="primary" onClick={this.handleReset}>
                العودة للصفحة الرئيسية
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                إعادة تحميل الصفحة
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

